import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import {
  useGetAgentLineupsQuery,
  useGetProjectLineupOverridesQuery,
  useResetPlatformLineupMutation,
  useResetProjectLineupMutation,
  useSavePlatformLineupMutation,
  type AgentLineup,
  type LineupEligibility,
  type LineupEntry,
  type LineupEntryInput,
  type LineupTier,
} from '../store/api/modelPerformanceApi'

/**
 * Agent lineup governance.
 *
 * A lineup is the ordered list of models one specialist may run on, in two
 * tiers: PRIMARY models may own a task and be accountable for the result,
 * DELEGATE models take narrow parallel sub-tasks handed down by a primary. The
 * split is about authority, not capability — a delegate may well be the better
 * coder.
 *
 * Three things may change a lineup, and the UI has to keep them distinguishable
 * or nobody will trust it:
 *
 *   measured performance   reorders within a tier. Nothing else, ever.
 *   a project             reorders within a tier, and may demote to backup.
 *   an admin (this screen) may also move a model between tiers.
 *
 * That last one is the reason this screen exists, and the reason a tier change
 * asks for confirmation: it is the only action here that grants or revokes a
 * model's authority to own work, and no amount of good telemetry can do it.
 */

const TIERS: LineupTier[] = ['primary', 'delegate']

const ELIGIBILITIES: Array<{ value: LineupEligibility; label: string; hint: string }> = [
  { value: 'candidate', label: 'Candidate', hint: 'Routable normally. May be chosen first and may be the default.' },
  { value: 'backup', label: 'Backup', hint: 'Reachable only by failover. Never chosen while a candidate is available.' },
  { value: 'experimental', label: 'Experimental', hint: 'Routable inside the exploration budget until it has a record.' },
  { value: 'disabled', label: 'Disabled', hint: 'Listed so the absence can be explained. Never routed to.' },
]

const TIER_BLURB: Record<LineupTier, string> = {
  primary: 'Models allowed to own the task end to end — understand it, decide what to do, and answer for the result.',
  delegate: 'Models a primary hands narrow, parallelisable sub-tasks to. Cheap enough to run several at once.',
}

function agentLabel(key: string): string {
  return key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** Priority is implied by array order, so the wire format drops it. */
function toInput(entry: LineupEntry): LineupEntryInput {
  return {
    modelKey: entry.modelKey,
    tier: entry.tier,
    eligibility: entry.eligibility,
    enabled: entry.enabled,
    isDefault: entry.isDefault,
  }
}

function sameLineup(a: LineupEntryInput[], b: LineupEntryInput[]): boolean {
  if (a.length !== b.length) return false
  return a.every((row, i) => (
    row.modelKey === b[i].modelKey
    && row.tier === b[i].tier
    && row.eligibility === b[i].eligibility
    && row.enabled === b[i].enabled
    && row.isDefault === b[i].isDefault
  ))
}

function Pill({ tone, children }: { tone: 'indigo' | 'amber' | 'gray' | 'violet'; children: React.ReactNode }) {
  const tones = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-600',
    violet: 'bg-violet-50 border-violet-200 text-violet-800',
  } as const
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tones[tone]}`}>
      {children}
    </span>
  )
}

interface EditorProps {
  lineup: AgentLineup
  catalog: Array<{ modelKey: string; label: string; provider: string | null }>
  onSaved: () => void
}

function LineupEditor({ lineup, catalog, onSaved }: EditorProps) {
  const shippedInput = useMemo(() => lineup.shipped.map(toInput), [lineup.shipped])
  const currentInput = useMemo(
    () => (lineup.customized ? lineup.platformOverride : lineup.shipped).map(toInput),
    [lineup],
  )

  const [draft, setDraft] = useState<LineupEntryInput[]>(currentInput)
  const [note, setNote] = useState('')
  const [addKey, setAddKey] = useState('')
  const [confirmTier, setConfirmTier] = useState<{ index: number; to: LineupTier } | null>(null)

  // Re-seed when the server's answer changes under us (a save, or another
  // admin's edit arriving on refetch). Keyed on the resolved lineup so a local
  // edit in progress is not wiped by an unrelated poll.
  useEffect(() => { setDraft(currentInput) }, [currentInput])

  const [save, { isLoading: saving }] = useSavePlatformLineupMutation()
  const [reset, { isLoading: resetting }] = useResetPlatformLineupMutation()

  const dirty = !sameLineup(draft, currentInput)
  const labelFor = (key: string) => catalog.find((m) => m.modelKey === key)?.label || key
  const providerFor = (key: string) => catalog.find((m) => m.modelKey === key)?.provider || null

  const shippedKeys = useMemo(
    () => new Map(shippedInput.map((row, i) => [row.modelKey, { ...row, position: i }])),
    [shippedInput],
  )

  function update(index: number, patch: Partial<LineupEntryInput>) {
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  /** Exactly one default per tier, so setting one clears the other. */
  function makeDefault(index: number) {
    setDraft((rows) => {
      const tier = rows[index].tier
      return rows.map((row, i) => (
        row.tier === tier ? { ...row, isDefault: i === index } : row
      ))
    })
  }

  /** Move within the tier. Order across tiers is meaningless. */
  function move(index: number, direction: -1 | 1) {
    setDraft((rows) => {
      const tier = rows[index].tier
      const siblings = rows.map((row, i) => ({ row, i })).filter((r) => r.row.tier === tier)
      const at = siblings.findIndex((r) => r.i === index)
      const swapWith = siblings[at + direction]
      if (!swapWith) return rows
      const next = [...rows]
      next[index] = swapWith.row
      next[swapWith.i] = rows[index]
      return next
    })
  }

  function remove(index: number) {
    setDraft((rows) => rows.filter((_, i) => i !== index))
  }

  function add() {
    if (!addKey) return
    if (draft.some((row) => row.modelKey === addKey)) {
      toast.error(`${labelFor(addKey)} is already in this lineup.`)
      return
    }
    setDraft((rows) => [
      ...rows,
      { modelKey: addKey, tier: 'delegate', eligibility: 'candidate', enabled: true, isDefault: false },
    ])
    setAddKey('')
  }

  function requestTierChange(index: number, to: LineupTier) {
    if (draft[index].tier === to) return
    setConfirmTier({ index, to })
  }

  function applyTierChange() {
    if (!confirmTier) return
    const { index, to } = confirmTier
    setDraft((rows) => {
      const next = rows.map((row, i) => (
        i === index ? { ...row, tier: to, isDefault: false } : row
      ))
      // The tier it left may have just lost its starting model.
      const from = rows[index].tier
      if (rows[index].isDefault) {
        const heir = next.find((row) => row.tier === from && row.enabled && row.eligibility === 'candidate')
          || next.find((row) => row.tier === from && row.enabled)
        if (heir) heir.isDefault = true
      }
      return next
    })
    setConfirmTier(null)
  }

  async function onSave() {
    try {
      await save({
        collaboratorKey: lineup.collaboratorKey,
        entries: draft,
        note: note.trim() || undefined,
      }).unwrap()
      // The API caches lineups in-process and this is a different service, so
      // the change lands on a TTL rather than instantly. Saying "saved" and
      // meaning "in about a minute" is how someone ends up convinced the button
      // does not work.
      toast.success(
        `Saved the ${agentLabel(lineup.collaboratorKey)} lineup. Runs starting within the next minute may still use the old order.`,
      )
      setNote('')
      onSaved()
    } catch (err: any) {
      toast.error(err?.data?.message || 'Could not save that lineup.')
    }
  }

  async function onReset() {
    try {
      await reset(lineup.collaboratorKey).unwrap()
      toast.success(
        `${agentLabel(lineup.collaboratorKey)} is back on the shipped lineup, in effect within a minute.`,
      )
      onSaved()
    } catch {
      toast.error('Could not reset that lineup.')
    }
  }

  const unlisted = catalog.filter((m) => !draft.some((row) => row.modelKey === m.modelKey))

  return (
    <div className="space-y-4">
      {TIERS.map((tier) => {
        const rows = draft.map((row, i) => ({ row, i })).filter((r) => r.row.tier === tier)
        const siblings = rows.length
        return (
          <div key={tier}>
            <div className="flex items-baseline gap-2 mb-1.5">
              <h4 className="text-sm font-semibold text-gray-900 capitalize">{tier}</h4>
              <span className="text-xs text-gray-500">{TIER_BLURB[tier]}</span>
            </div>

            {!siblings && (
              <p className="text-xs text-gray-500 italic border border-dashed border-gray-300 rounded-lg px-3 py-2">
                Empty. The shipped {tier} tier will be used instead — a specialist is never left with
                nowhere to run.
              </p>
            )}

            <div className="space-y-1.5">
              {rows.map(({ row, i }, positionInTier) => {
                const shipped = shippedKeys.get(row.modelKey)
                const movedTier = shipped && shipped.tier !== row.tier
                const isNew = !shipped
                return (
                  <div
                    key={row.modelKey}
                    className={`flex items-center gap-2 flex-wrap border rounded-lg px-2.5 py-2 ${
                      row.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <span className="text-xs text-gray-400 w-5 tabular-nums">{positionInTier + 1}</span>

                    <div className="flex flex-col gap-0.5 min-w-[11rem]">
                      <span className={`text-sm font-medium ${row.enabled ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                        {labelFor(row.modelKey)}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {providerFor(row.modelKey) || row.modelKey}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => move(i, -1)}
                        disabled={positionInTier === 0}
                        title="Earlier in this tier"
                        className="p-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <ArrowUpIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => move(i, 1)}
                        disabled={positionInTier === siblings - 1}
                        title="Later in this tier"
                        className="p-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <ArrowDownIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <select
                      value={row.tier}
                      onChange={(e) => requestTierChange(i, e.target.value as LineupTier)}
                      title="Which tier this model belongs to. Only an admin can change this."
                      className="border border-gray-300 rounded px-1.5 py-1 text-xs bg-white capitalize"
                    >
                      {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <select
                      value={row.eligibility}
                      onChange={(e) => update(i, {
                        eligibility: e.target.value as LineupEligibility,
                        // "Disabled" and "switched off" are the same instruction
                        // said two ways; keeping them in step avoids a row that
                        // claims to be routable and is not.
                        enabled: e.target.value !== 'disabled' ? true : false,
                        isDefault: e.target.value === 'disabled' ? false : row.isDefault,
                      })}
                      title={ELIGIBILITIES.find((e) => e.value === row.eligibility)?.hint}
                      className="border border-gray-300 rounded px-1.5 py-1 text-xs bg-white"
                    >
                      {ELIGIBILITIES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                    </select>

                    <label
                      className="flex items-center gap-1 text-xs text-gray-600"
                      title="The model this specialist starts on. One per tier."
                    >
                      <input
                        type="radio"
                        name={`${lineup.collaboratorKey}-${tier}-default`}
                        checked={row.isDefault}
                        disabled={!row.enabled || row.eligibility === 'backup' || row.eligibility === 'disabled'}
                        onChange={() => makeDefault(i)}
                        className="accent-indigo-600"
                      />
                      Starts here
                    </label>

                    <div className="flex items-center gap-1 ml-auto">
                      {movedTier && <Pill tone="amber">moved from {shipped!.tier}</Pill>}
                      {isNew && <Pill tone="violet">added</Pill>}
                      {row.modelKey.includes('terra') || row.modelKey.includes('sol') || row.modelKey.includes('fable') || row.modelKey.includes('opus-5')
                        ? <Pill tone="indigo">premium</Pill>
                        : null}
                      <button
                        onClick={() => remove(i)}
                        title="Remove from this lineup"
                        className="p-1 rounded border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* ── Add a model ── */}
      <div className="flex items-end gap-2 flex-wrap pt-1">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium">Add a model</span>
          <select
            value={addKey}
            onChange={(e) => setAddKey(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white min-w-[14rem]"
          >
            <option value="">Choose a model…</option>
            {unlisted.map((m) => (
              <option key={m.modelKey} value={m.modelKey}>
                {m.label}{m.provider ? ` · ${m.provider}` : ''}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={add}
          disabled={!addKey}
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40"
        >
          <PlusIcon className="w-4 h-4" /> Add as delegate
        </button>
      </div>

      {/* ── Save ── */}
      <div className="flex items-end gap-2 flex-wrap border-t border-gray-200 pt-3">
        <label className="flex flex-col gap-1 flex-1 min-w-[16rem]">
          <span className="text-xs text-gray-500 font-medium">
            Why this differs from the shipped lineup (recorded with the change)
          </span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Sonnet has been beating Pro on design work for six weeks"
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
          />
        </label>
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="px-3.5 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save lineup'}
        </button>
        {lineup.customized && (
          <button
            onClick={onReset}
            disabled={resetting}
            title="Delete the override so the shipped lineup applies again"
            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            <ArrowUturnLeftIcon className="w-4 h-4" /> Reset to shipped
          </button>
        )}
        {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
      </div>

      {/* ── Tier change confirmation ── */}
      {confirmTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-base font-semibold text-gray-900">
                  Move {labelFor(draft[confirmTier.index].modelKey)} to {confirmTier.to}?
                </h4>
                <p className="text-sm text-gray-600 mt-1.5">
                  {confirmTier.to === 'primary' ? (
                    <>
                      This lets the model <strong>own</strong> {agentLabel(lineup.collaboratorKey)} tasks
                      and be accountable for the result, rather than only taking sub-tasks handed to it.
                      No amount of measured performance can do this on its own — it is a judgement about
                      authority, not capability.
                    </>
                  ) : (
                    <>
                      This <strong>revokes</strong> the model's authority to own {agentLabel(lineup.collaboratorKey)}{' '}
                      tasks. It will only receive sub-tasks from a primary. Any project currently
                      starting on it will move to the next primary model.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirmTier(null)}
                className="px-3.5 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={applyTierChange}
                className="px-3.5 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700"
              >
                Move to {confirmTier.to}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const AgentLineupsTab: React.FC = () => {
  const { data: resp, isLoading, isError, refetch } = useGetAgentLineupsQuery()
  const lineups = resp?.data?.lineups ?? []
  const catalog = resp?.data?.catalog ?? []

  const [selected, setSelected] = useState<string>('')
  const active = lineups.find((l) => l.collaboratorKey === selected) || lineups[0]

  useEffect(() => {
    if (!selected && lineups.length) setSelected(lineups[0].collaboratorKey)
  }, [lineups, selected])

  const { data: projectResp } = useGetProjectLineupOverridesQuery(
    active ? { collaboratorKey: active.collaboratorKey } : undefined,
    { skip: !active },
  )
  const projectOverrides = projectResp?.data?.overrides ?? []
  const [resetProject] = useResetProjectLineupMutation()

  const publishedAt = active?.publishedAt ? new Date(active.publishedAt) : null
  const stale = publishedAt ? Date.now() - publishedAt.getTime() > 7 * 86_400_000 : false

  if (isLoading) {
    return <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-500">Loading lineups…</div>
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
        Could not load agent lineups.
        <button onClick={() => refetch()} className="ml-2 underline">Try again</button>
      </div>
    )
  }

  if (!lineups.length) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <p className="text-sm font-medium text-amber-900">No lineups have been published yet.</p>
        <p className="text-xs text-amber-800 mt-1.5 max-w-2xl">
          The API publishes its code-defined lineups into <code>cat_agent_model_lineups</code> on
          startup, which is what this screen reads — the lineups themselves live in the API's code
          because adding a model needs provider dispatch, a key and a credit rate. If this stays
          empty, check that migration <code>318_agent_lineup_governance.sql</code> has run and that
          the API has restarted since.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* ── Explanation ── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <h3 className="text-base font-semibold text-gray-900">Agent lineups</h3>
        <p className="text-xs text-gray-500 mt-1 max-w-4xl">
          The ordered list of models each specialist may run on. <strong>Primary</strong> models may
          own a task and answer for the result; <strong>delegate</strong> models take narrow
          sub-tasks a primary hands down. The split is about authority, not capability — a delegate
          is often the better coder for a given job.
        </p>
        <p className="text-xs text-gray-500 mt-2 max-w-4xl">
          Measured performance reorders models <em>within</em> a tier and does nothing else. A
          project may also reorder within a tier and demote a model to a backup. Moving a model
          between tiers is only possible here, which is why it asks for confirmation.
        </p>
        {publishedAt && (
          <p className={`text-xs mt-2.5 ${stale ? 'text-amber-700' : 'text-gray-400'}`}>
            Shipped lineups last published by the API {publishedAt.toLocaleString()}
            {stale && ' — that is over a week ago, so this may not reflect the current deploy.'}
          </p>
        )}
      </div>

      {/* ── Specialist picker ── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 flex flex-wrap gap-1.5">
        {lineups.map((l) => {
          const isActive = active?.collaboratorKey === l.collaboratorKey
          return (
            <button
              key={l.collaboratorKey}
              onClick={() => setSelected(l.collaboratorKey)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                isActive
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {agentLabel(l.collaboratorKey)}
              {l.customized && (
                <span className={`ml-1.5 text-[10px] ${isActive ? 'text-indigo-100' : 'text-amber-600'}`}>
                  edited
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* ── Editor ── */}
      {active && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{agentLabel(active.collaboratorKey)}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {active.customized
                  ? 'Running on an admin override. The shipped lineup is shown below for comparison.'
                  : 'Running on the lineup the API ships. Saving any change creates an override.'}
              </p>
            </div>
            {active.customized && <Pill tone="amber">overridden</Pill>}
          </div>

          <LineupEditor
            key={`${active.collaboratorKey}:${active.customized}`}
            lineup={active}
            catalog={catalog}
            onSaved={refetch}
          />

          {active.customized && active.shipped.length > 0 && (
            <div className="mt-5 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-900">What the API ships</p>
              <p className="text-xs text-gray-500 mb-2">
                For comparison. An override nobody can compare to the default is an override nobody
                will dare remove.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {TIERS.map((tier) => (
                  <div key={tier} className="text-xs text-gray-600">
                    <span className="font-medium capitalize">{tier}:</span>{' '}
                    {active.shipped
                      .filter((e) => e.tier === tier)
                      .map((e) => `${e.label || e.modelKey}${e.isDefault ? ' (starts)' : ''}`)
                      .join(' → ') || '—'}
                    {tier === 'primary' && <span className="mx-2 text-gray-300">|</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Projects that have overridden this specialist ── */}
      {active && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-900">
            Projects overriding {agentLabel(active.collaboratorKey)}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 mb-3 max-w-3xl">
            A platform change that appears to have no effect is usually a change to something these
            projects have already decided for themselves. Their overrides win.
          </p>

          {!projectOverrides.length ? (
            <p className="text-xs text-gray-500 italic">
              None. Every project follows the platform lineup for this specialist.
            </p>
          ) : (
            <div className="space-y-1.5">
              {projectOverrides.map((o) => (
                <div
                  key={`${o.projectId}-${o.collaboratorKey}`}
                  className="flex items-center gap-3 flex-wrap border border-gray-200 rounded-lg px-3 py-2"
                >
                  <span className="text-sm font-medium text-gray-900 min-w-[10rem]">
                    {o.projectName || `Project ${o.projectId}`}
                  </span>
                  <span className="text-xs text-gray-500 flex-1 min-w-[14rem]">
                    {TIERS.map((tier) => {
                      const inTier = o.entries.filter((e) => e.tier === tier)
                      if (!inTier.length) return null
                      return (
                        <span key={tier} className="mr-3">
                          <span className="capitalize font-medium">{tier}</span>:{' '}
                          {inTier.map((e) => `${e.label || e.modelKey}${e.isDefault ? ' (starts)' : ''}`).join(' → ')}
                        </span>
                      )
                    })}
                  </span>
                  <button
                    onClick={async () => {
                      try {
                        await resetProject({
                          projectId: o.projectId,
                          collaboratorKey: o.collaboratorKey,
                        }).unwrap()
                        toast.success(`${o.projectName || `Project ${o.projectId}`} follows the platform lineup again.`)
                      } catch {
                        toast.error('Could not clear that override.')
                      }
                    }}
                    className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50"
                  >
                    <ArrowUturnLeftIcon className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AgentLineupsTab
